import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ModulePermissions, PermissionLevel } from '../types';

/**
 * Hook centralizado para verificaciones de permisos y roles.
 * Reemplaza validaciones repetidas como isAdmin, canExport, etc.
 *
 * @example
 * const { isAdmin, canExport, canManageUsers, hasFullAccess } = useRolePermissions()
 * if (isAdmin) { ... }
 * if (canExport('appointments')) { ... }
 */
export function useRolePermissions() {
  const {
    user,
    permissions
  } = useAuth();
  const roleChecks = useMemo(() => {
    if (!user || !permissions) {
      return {
        isAdmin: false,
        isDoctor: false,
        isTherapist: false,
        isReceptionist: false,
        isCallCenter: false,
        isSupervisor: false,
        canExport: () => false,
        canManageUsers: false,
        canManageRoles: false,
        canManageBackups: false,
        canViewAuditLog: false,
        canViewIntelligence: false,
        canViewCommissions: false,
        hasFullAccess: () => false,
        hasReadAccess: () => false,
        hasOwnAccess: () => false,
        getPermissionLevel: () => 'none' as PermissionLevel
      };
    }
    const role = user.role.toLowerCase();
    const normalizedRole = role.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

    // Role checks
    const isAdmin = role === 'administrador';
    const isDoctor = role === 'doctor' || role === 'doctora';
    const isTherapist = role === 'terapeuta' || role === 'terapista';
    const isReceptionist = normalizedRole === 'recepcion' || normalizedRole === 'recepcionista';
    const isCallCenter = role === 'callcenter' || role === 'call center' || role === 'call_center';
    const isSupervisor = role === 'supervisor' || role === 'supervisor/a';

    // Export permissions (Excel/PDF)
    const canExportRoles = new Set(['administrador', 'terapeuta', 'terapista', 'supervisor', 'supervisor/a', 'recepcion', 'recepcionista']);
    const canExport = (module: keyof ModulePermissions) => {
      if (isAdmin) return true;
      if (!canExportRoles.has(role) && !canExportRoles.has(normalizedRole)) return false;
      const level = permissions[module];
      return level === 'full' || level === 'read' || level === 'own';
    };

    // Admin-level permissions
    const canManageUsers = permissions.settings_users === 'full';
    const canManageRoles = permissions.settings_roles_permissions === 'full';
    const canManageBackups = permissions.settings_backups === 'full';
    const canViewAuditLog = permissions.auditLog !== 'none';
    const canViewIntelligence = permissions.intelligence !== 'none';
    const canViewCommissions = permissions.commissions !== 'none';

    // Permission level helpers
    const hasFullAccess = (module: keyof ModulePermissions) => permissions[module] === 'full';
    const hasReadAccess = (module: keyof ModulePermissions) => {
      const level = permissions[module];
      return level === 'full' || level === 'read' || level === 'own';
    };
    const hasOwnAccess = (module: keyof ModulePermissions) => permissions[module] === 'own';
    const getPermissionLevel = (module: keyof ModulePermissions): PermissionLevel => permissions[module];
    return {
      isAdmin,
      isDoctor,
      isTherapist,
      isReceptionist,
      isCallCenter,
      isSupervisor,
      canExport,
      canManageUsers,
      canManageRoles,
      canManageBackups,
      canViewAuditLog,
      canViewIntelligence,
      canViewCommissions,
      hasFullAccess,
      hasReadAccess,
      hasOwnAccess,
      getPermissionLevel
    };
  }, [user, permissions]);
  return roleChecks;
}